import { Box, Grid, Typography } from "@mui/material";
import { isNil } from "lodash-es";
import { useState } from "react";

import PageTabContent from "@/App/Layouts/PageBody/PageTabContent";
import PageTabHeader from "@/App/Layouts/PageBody/PageTabHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import ViewContentBlockHeader from "@/App/Layouts/ViewContentBlockHeader";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import NoDataAlert from "@/common/components/AppAlerts/NoDataAlert";
import AuthorizedTenantElement from "@/common/components/Auth/AuthorizedTenantElement";
import AppIconButton from "@/common/components/Button/AppIconButton";
import Datetime from "@/common/components/Datetime/Datetime";
import FoldableBlock from "@/common/components/Display/FoldableBlock";
import GeneralPlateNoDisplay from "@/common/components/Entity/General/Display/GeneralPlateNoDisplay";
import { VisualModelImagesList } from "@/common/components/Entity/VehicleVisualModel/Viewer/v1/VisualModelImagesList";
import EntityCreatedByInfoCard from "@/common/components/EntityInfo/EntityCreatedByInfoCard";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import InfoTile from "@/common/components/Form/Display/InfoTile";
import AppIcon from "@/common/components/Icons/AppIcon";
import BaseEditMileageModal from "@/common/components/Modals/common/BaseEditMileageModal";
import BaseEditNotesModal from "@/common/components/Modals/common/BaseEditNotesModal";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import { AppPermission, EntityType, VehicleDto } from "@/core/api/generated/v1";

import ContractLink from "../../../Contract/ContractLink";
import EntityAffiliationInfoBlock from "../../../EntityAffiliation/EntityAffiliationInfoBlock";
import GeneralGeolocationDisplay from "../../../General/GeneralGeolocation/GeneralGeolocationDisplay";
import GeneralGeolocationOfEntityUpdateModal from "../../../General/GeneralGeolocation/GeneralGeolocationOfEntityUpdateModal";
import GeneralProductProfileDisplay from "../../../General/GeneralProductProfile/GeneralProductProfileDisplay";
import GeneralProductProfileOfEntityUpdateModal from "../../../General/GeneralProductProfile/GeneralProductProfileOfEntityUpdateModal";
import GeneralStatusMetaDisplayAndInputOfVehicle from "../../../GeneralStatus/common/GeneralStatusMetaDisplayAndInputOfVehicle";
import GeneralStatusMetaDisplayOfAvailabilityStatus from "../../../GeneralStatus/common/GeneralStatusMetaDisplayOfAvailabilityStatus";
import GeneralStatusMetaDisplayOfOperabilityStatus from "../../../GeneralStatus/common/GeneralStatusMetaDisplayOfOperabilityStatus";
import GeneralStatusMetaDisplayOfUtilizationStatus from "../../../GeneralStatus/common/GeneralStatusMetaDisplayOfUtilizationStatus";
import VehicleBatteryStateDisplay from "../../VehicleBatteryStateDisplay";
import VehiclePriceDisplay from "../../VehiclePriceDisplay";
import VehicleSpecDisplay from "../../VehicleSpecDisplay";

interface Props {
  vehicle: VehicleDto;
  onUpdate: (newValue?: VehicleDto) => any | void;
}

export default function OverviewTabContent({ vehicle, onUpdate }: Props) {
  const [isUpdateMileageModalOpened, setIsUpdateMileageModalOpened] = useState(false);
  const [isGeolocationUpdateModalOpen, setIsGeolocationUpdateModalOpen] = useState(false);
  const [isProductProfileUpdateModalOpen, setIsProductProfileUpdateModalOpen] = useState(false);

  const defaultVehicleVisualModelRequest = useApiRequest(
    apiClient.vehicleVisualModelsApi.apiV1ReferenceDataVehiclesVisualModelsModelIdGet,
    {
      modelId: vehicle.defaultVisualModel?.id || "",
    },
    {
      deps: [vehicle.defaultVisualModel?.id],
      skip: !vehicle.defaultVisualModel?.id,
    },
  );
  const defaultVehicleVisualModel = defaultVehicleVisualModelRequest?.data;

  return (
    <PageTabContent>
      <ViewLayout
        displayProps={{ viewVariant: ViewVariant.Tab }}
        header={
          <PageTabHeader
            title={"Details"}
            subtitle2={<EntityCreatedByInfoCard entity={vehicle} />}
          />
        }
      >
        <ViewContentBlock>
          <Box
            sx={{
              mt: 1,
              display: "grid",
              gridTemplateColumns: {
                xxs: "1fr",
                sm: "repeat(auto-fill, minmax(20%, 1fr))",
                md: "repeat(auto-fill, minmax(20%, 1fr))",
                lg: "repeat(auto-fill, minmax(20%, 1fr))",
              },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            <InfoTile label='Plate No' iconOf='plateNo' isEmpty={!vehicle.plateNo}>
              <GeneralPlateNoDisplay plateNo={vehicle.plateNo} withIcon={false} withCountryCode />
            </InfoTile>

            <InfoTile label='ID number' iconOf='VIN' isEmpty={!vehicle.identificationNumber}>
              {vehicle.identificationNumber}
            </InfoTile>

            <InfoTile label='Make' iconOf='vehicle'>
              {vehicle.spec?.make?.name}
            </InfoTile>

            <InfoTile label='Model' iconOf='vehicleSide'>
              {vehicle.spec?.model?.name}
            </InfoTile>

            <InfoTile label='Generation' iconOf='clock' isEmpty={!vehicle.spec?.generation}>
              {vehicle.spec?.generation?.name}
            </InfoTile>

            <InfoTile
              label='Modification'
              iconOf='modification'
              isEmpty={!vehicle.spec?.modification}
            >
              {vehicle.spec?.modification?.name}
            </InfoTile>

            <InfoTile
              label='Manufacture year'
              iconOf='document'
              isEmpty={!vehicle.spec?.manufactureYear}
            >
              {vehicle.spec?.manufactureYear}
            </InfoTile>

            <InfoTile label='Type' iconOf='data' isEmpty={!vehicle.spec?.type}>
              <InlineApiEnumValue type='VehicleType' value={vehicle.spec?.type} />
            </InfoTile>

            <InfoTile label='Size' iconOf='data' isEmpty={!vehicle.spec?.size}>
              <InlineApiEnumValue type='VehicleSize' value={vehicle.spec?.size} />
            </InfoTile>

            <InfoTile label='Body type' iconOf='repairCar' isEmpty={!vehicle.spec?.bodyType}>
              <InlineApiEnumValue type='VehicleBodyType' value={vehicle.spec?.bodyType} />
            </InfoTile>

            <InfoTile label='Fuel type' iconOf='fuel' isEmpty={!vehicle.spec?.fuelType}>
              <InlineApiEnumValue type='VehicleFuelType' value={vehicle.spec?.fuelType} />
            </InfoTile>

            <InfoTile
              label='Mileage'
              iconOf='gauge'
              isEmpty={!vehicle.mileage}
              endAdornment={
                <AuthorizedTenantElement tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}>
                  <BaseEditMileageModal
                    vehicle={vehicle}
                    mileage={vehicle.mileage}
                    trigger={
                      <AppIconButton
                        sx={{ p: "2px", ml: 0.5 }}
                        onClick={() => setIsUpdateMileageModalOpened(true)}
                      >
                        <AppIcon of='edit' sx={{ width: "0.8em", height: "0.8em" }} />
                      </AppIconButton>
                    }
                    triggerParams={{ isDisableDefaultAction: true }}
                    open={isUpdateMileageModalOpened}
                    onClose={() => setIsUpdateMileageModalOpened(false)}
                    onConfirm={async (mileage) => {
                      await apiClient.vehiclesApi.apiV1VehiclesVehicleIdMileagePut({
                        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                        vehicleId: vehicle.id || "",
                        updateVehicleMileageDto: { mileage: mileage },
                      });
                    }}
                    onMileageChanged={() => onUpdate(undefined)}
                  />
                </AuthorizedTenantElement>
              }
            >
              {vehicle.mileage?.mileage} {vehicle.mileage?.unit}
            </InfoTile>

            <InfoTile label='Actual damages' iconOf='damage'>
              {vehicle.actualDamagesCount}
            </InfoTile>

            <InfoTile label='Repaired damages' iconOf='damage'>
              {vehicle.repairedDamagesCount}
            </InfoTile>

            <InfoTile
              label='Attachments'
              iconOf='attachments'
              isEmpty={!vehicle.attachments || vehicle.attachments?.length === 0}
            >
              {vehicle.attachments?.length}
            </InfoTile>

            <AuthorizedTenantElement tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}>
              <InfoTile
                label='Last damage detection'
                iconOf='damageTime'
                isEmpty={!vehicle.lastDamageDetectionAt}
              >
                <Datetime datetime={vehicle.lastDamageDetectionAt} />
              </InfoTile>
            </AuthorizedTenantElement>

            <AuthorizedTenantElement tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}>
              <InfoTile
                label='Last accessory check'
                iconOf='accessory'
                isEmpty={!vehicle.lastAccessoryCheckAt}
              >
                <Datetime datetime={vehicle.lastAccessoryCheckAt} />
              </InfoTile>
            </AuthorizedTenantElement>

            <AuthorizedTenantElement tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}>
              <InfoTile
                label='Last mileage update'
                iconOf='gaugeTime'
                isEmpty={!vehicle.lastMileageUpdatedAt}
              >
                <Datetime datetime={vehicle.lastMileageUpdatedAt} />
              </InfoTile>
            </AuthorizedTenantElement>

            <AuthorizedTenantElement tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}>
              <InfoTile label='Last contract' iconOf='contract' isEmpty={!vehicle.lastContract}>
                <ContractLink entity={vehicle.lastContract} />
              </InfoTile>
            </AuthorizedTenantElement>

            <AuthorizedTenantElement tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}>
              <Grid size={{ xs: 12, md: 3 }}>
                <InfoTile
                  label='Purchase price'
                  iconOf='price'
                  isEmpty={isNil(
                    vehicle.spec?.purchasePrice?.price ?? vehicle.spec?.purchasingCost,
                  )}
                >
                  <VehiclePriceDisplay
                    price={vehicle.spec?.purchasePrice}
                    fallbackPrice={{ price: vehicle.spec?.purchasingCost ?? undefined }}
                  />
                </InfoTile>
              </Grid>
            </AuthorizedTenantElement>

            <AuthorizedTenantElement tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}>
              <InfoTile label='Status' iconOf='status' isEmpty={!vehicle.status}>
                <GeneralStatusMetaDisplayAndInputOfVehicle
                  vehicle={vehicle}
                  onVehicleUpdated={onUpdate}
                />
              </InfoTile>
            </AuthorizedTenantElement>

            <AuthorizedTenantElement tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}>
              <InfoTile label='Availability status' iconOf='status'>
                <GeneralStatusMetaDisplayOfAvailabilityStatus
                  value={vehicle.availabilityStatus}
                  subjectEntity={vehicle}
                  onMetaUpdated={(newValue) => {
                    onUpdate({
                      ...vehicle,
                      availabilityStatus: newValue,
                    });
                  }}
                />
              </InfoTile>
            </AuthorizedTenantElement>

            <AuthorizedTenantElement tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}>
              <InfoTile label='Utilization status' iconOf='status'>
                <GeneralStatusMetaDisplayOfUtilizationStatus
                  value={vehicle.utilizationStatus}
                  subjectEntity={vehicle}
                  onMetaUpdated={(newValue) => {
                    onUpdate({
                      ...vehicle,
                      utilizationStatus: newValue,
                    });
                  }}
                />
              </InfoTile>
            </AuthorizedTenantElement>

            <AuthorizedTenantElement tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}>
              <InfoTile label='Operability status' iconOf='status'>
                <GeneralStatusMetaDisplayOfOperabilityStatus
                  value={vehicle.operabilityStatus}
                  subjectEntity={vehicle}
                  onMetaUpdated={(newValue) => {
                    onUpdate({
                      ...vehicle,
                      operabilityStatus: newValue,
                    });
                  }}
                />
              </InfoTile>
            </AuthorizedTenantElement>

            <InfoTile
              label='Notes'
              iconOf='notes'
              isEmpty={!vehicle.notes}
              endAdornment={
                <AuthorizedTenantElement tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}>
                  <BaseEditNotesModal
                    initialValues={{ notes: vehicle.notes }}
                    onConfirm={async (notes) => {
                      await apiClient.vehiclesApi.apiV1VehiclesVehicleIdNotesPut({
                        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                        vehicleId: vehicle.id || "",
                        generalNotesInputDto: { notes },
                      });
                      onUpdate(undefined);
                    }}
                  />
                </AuthorizedTenantElement>
              }
            >
              {vehicle.notes}
            </InfoTile>
          </Box>
        </ViewContentBlock>

        {/* Spec */}
        <ViewContentBlock>
          <FoldableBlock
            defaultIsFolded
            trigger={{
              label: <ViewContentBlockHeader>Full spec</ViewContentBlockHeader>,
            }}
          >
            <VehicleSpecDisplay tenantId={vehicle.tenantId} spec={vehicle.spec} />
          </FoldableBlock>
        </ViewContentBlock>

        {/* Battery state */}
        {vehicle.batteryState && (
          <ViewContentBlock>
            <FoldableBlock
              sx={{ my: 1 }}
              defaultIsFolded
              trigger={{
                label: <ViewContentBlockHeader>Battery state</ViewContentBlockHeader>,
              }}
            >
              <VehicleBatteryStateDisplay batteryState={vehicle.batteryState} />
            </FoldableBlock>
          </ViewContentBlock>
        )}

        {/* Affiliation */}
        <EntityAffiliationInfoBlock
          entityType={EntityType.Vehicle}
          entityId={vehicle.id}
          entity={vehicle}
          entityUpdatePermission={AppPermission.VehicleManage}
          infoDisplayProps={{
            ownerTenantId: vehicle?.ownerTenantId,
            withoutTenant: false,
          }}
          onUpdate={onUpdate}
        />

        <ViewContentBlock>
          <ViewContentBlockHeader>Default visual model</ViewContentBlockHeader>
          <Typography variant='body2'>(Used by default for all new damage detections)</Typography>

          {!vehicle.defaultVisualModel && <NoDataAlert title='No default visual model set.' />}

          {vehicle.defaultVisualModel && defaultVehicleVisualModel && (
            <Box>
              <Box>
                <VisualModelImagesList
                  visualModel={defaultVehicleVisualModel}
                  imageContainerSx={{ width: `100px`, height: `auto` }}
                />
              </Box>
            </Box>
          )}
        </ViewContentBlock>

        {/* Geolocation */}
        <AuthorizedTenantElement tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}>
          <ViewContentBlock>
            <FoldableBlock
              defaultIsFolded
              trigger={{
                label: (
                  <ViewContentBlockHeader>
                    Geolocation{" "}
                    <AuthorizedTenantElement
                      tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}
                    >
                      <AppIconButton
                        size='medium'
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsGeolocationUpdateModalOpen(true);
                        }}
                      >
                        <AppIcon of='edit' fontSize='small' />
                      </AppIconButton>
                    </AuthorizedTenantElement>
                  </ViewContentBlockHeader>
                ),
              }}
            >
              <Box>
                {!vehicle.geolocation && <NoDataAlert />}
                {vehicle.geolocation && <GeneralGeolocationDisplay value={vehicle.geolocation} />}
              </Box>
            </FoldableBlock>

            <AuthorizedTenantElement tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}>
              {isGeolocationUpdateModalOpen && (
                <GeneralGeolocationOfEntityUpdateModal
                  updateProps={{
                    initialValue: vehicle.geolocation,
                    saveFunc: async (newValue) => {
                      const response =
                        await apiClient.vehiclesApi.apiV1VehiclesVehicleIdGeolocationPut({
                          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                          vehicleId: vehicle.id || "",
                          generalGeolocationOfEntityUpdateDto: {
                            ...newValue,
                          },
                        });
                      return response.data;
                    },
                    onSaved: (newValue) => {
                      setIsGeolocationUpdateModalOpen(false);
                      onUpdate({
                        ...vehicle,
                        geolocation: newValue.geolocation,
                      });
                    },
                  }}
                  open={isGeolocationUpdateModalOpen}
                  onClose={() => {
                    setIsGeolocationUpdateModalOpen(false);
                  }}
                />
              )}
            </AuthorizedTenantElement>
          </ViewContentBlock>
        </AuthorizedTenantElement>

        {/* Product profile */}
        <AuthorizedTenantElement tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}>
          <ViewContentBlock>
            <FoldableBlock
              defaultIsFolded
              trigger={{
                label: (
                  <ViewContentBlockHeader>
                    Product profile{" "}
                    <AuthorizedTenantElement
                      tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}
                    >
                      <AppIconButton
                        size='medium'
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsProductProfileUpdateModalOpen(true);
                        }}
                      >
                        <AppIcon of='edit' fontSize='small' />
                      </AppIconButton>
                    </AuthorizedTenantElement>
                  </ViewContentBlockHeader>
                ),
              }}
            >
              <Box>
                {!vehicle.productProfile && <NoDataAlert />}
                {vehicle.productProfile && (
                  <GeneralProductProfileDisplay value={vehicle.productProfile} />
                )}
              </Box>
            </FoldableBlock>

            <AuthorizedTenantElement tenantIdsAny={[vehicle?.tenantId, vehicle?.ownerTenantId]}>
              {isProductProfileUpdateModalOpen && (
                <GeneralProductProfileOfEntityUpdateModal
                  updateProps={{
                    initialValue: vehicle.productProfile,
                    saveFunc: async (newValue) => {
                      const response =
                        await apiClient.vehiclesApi.apiV1VehiclesVehicleIdProductProfilePut({
                          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                          vehicleId: vehicle.id || "",
                          generalProductProfileOfEntityUpdateDto: {
                            ...newValue,
                          },
                        });
                      return response.data;
                    },
                    onSaved: (newValue) => {
                      setIsProductProfileUpdateModalOpen(false);
                      onUpdate({
                        ...vehicle,
                        productProfile: newValue.productProfile,
                      });
                    },
                  }}
                  open={isProductProfileUpdateModalOpen}
                  onClose={() => {
                    setIsProductProfileUpdateModalOpen(false);
                  }}
                />
              )}
            </AuthorizedTenantElement>
          </ViewContentBlock>
        </AuthorizedTenantElement>
      </ViewLayout>
    </PageTabContent>
  );
}
